import React, { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { MetadataContent } from '@jutro/uiconfig';
import { useTranslator } from '@jutro/locale';
import { useDependencies } from 'gw-portals-dependency-react';
import fileFormatsMap from './FileFormatsMap.json'
import _ from 'lodash';
import { useModal, IconButton } from '@jutro/components';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { ErrorCodes } from 'gw-portals-util-adac-js';
import { CommunicationVariablesContext } from 'gw-capability-fnol-adac-react';
import messages from './AdacDocumentsUpload.messages';
import metadata from './AdacDocumentsUpload.metadata.json5';
import styles from './AdacDocumentsUpload.module.scss';

const ILLEGAL_FORMAT_ERROR_CODE = 'GW_ILLEGAL_CONTENTTYPE_ERROR';
const MAX_UPLOAD_FILE_SIZE = 100000000;
const ALLOWED_FILE_FORMATS = ['jpg', 'jpeg', 'png', 'heic', 'bmp', 'pdf', 'heif', 'svg'];

function getDocumentFilterByServiceMode(serviceMode) {
    switch (serviceMode) {
        case 'medical':
        default:
            return 'Medical';
        case 'vehiclegermany':
        case 'vehicleabroad':
            return 'Vehicle';
    }
}

export async function onDocumentsUpload({
    documents,
    setDocumentsSending,
    setDocumentsSent,
    ClaimService,
    translator,
    token,
    history,
    updateDocumentsToSend,
    showAlert
}) {

    const getDocumentFailedString = (start, reason) => {
        const documentNotSend = [];
        let filesToDisplay = '';
        for (let step = start; step < documents.length; step += 1) {
            documentNotSend.push(documents[step].file.name);
        }
        filesToDisplay = documentNotSend.join('\n');

        switch (reason) {
            case ILLEGAL_FORMAT_ERROR_CODE:
                return translator(messages.failedUploadDocumentsFileFormat, {
                    documentArray: filesToDisplay
                });
            default:
                return translator(messages.failedUploadDocuments, {
                    documentArray: filesToDisplay
                });
        }
    };

    const handleError = (title, message) => {
        return showAlert({
            title: title,
            message: message,
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok
        }).catch(_.noop);
    };

    setDocumentsSending(true);
    let documentSendBeforeFail = 0;
    try {
        for (let doc of documents) {
            await ClaimService.uploadDocument(
                doc.file,
                doc
            );
            documentSendBeforeFail += 1;
        }
        setDocumentsSent(true);
    } catch (error) {
        if (error.appErrorCode === ErrorCodes.SESSION_EXPIRED) {
            history.push({
                pathname: '/sessionexpired'
            });
        } else {
            handleError(
                commonMessages.errorUploadTitle,
                getDocumentFailedString(documentSendBeforeFail, error.errorCode)
            );
        }
    } finally {
        updateDocumentsToSend(
            documents.splice(
                documentSendBeforeFail, documents.length - documentSendBeforeFail
            )
        );
        setDocumentsSending(false);
    }
}

function DocumentsUpload({
    location,
    updateDocumentsToSend,
    documentsToSend,
    hideCancel,
    outerCancel
}) {
    const { communicationVariables } = useContext(CommunicationVariablesContext)
    const { token, sessionUUID, communicationTypeExt } = communicationVariables;

    const translator = useTranslator();
    const [newDocument, setNewDocument] = useState(false);
    const [documentCategory, setDocumentCategory] = useState(null);
    const viewModelService = useContext(ViewModelServiceContext);
    const { ClaimService } = useDependencies('ClaimService');
    const { showAlert } = useModal();
    const documentTypes = useMemo(() => {
        if (!viewModelService) return [];
        const typelist = viewModelService.productMetadata.get('cc').types.getTypelist('typekey.PortalDocumentType_Adac').getFilter(getDocumentFilterByServiceMode(communicationTypeExt));
        return typelist.codes
            .map((typecode) => ({
                ...typecode,
                name: translator({
                    id: `typekey.PortalDocumentType_Adac.${typecode.code}`,
                    defaultMessage: typecode.name
                })
            }));
    }, [viewModelService, translator]);

    const removeDocument = useCallback((item) => {
        updateDocumentsToSend((prevDocs) => prevDocs.filter((doc) => doc.sessionID !== item.sessionID));
    }, [updateDocumentsToSend]);

    const handleError = useCallback(
        (title, message) => {
            return showAlert({
                title: title,
                message: message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }, []
    );

    const getRemove = useCallback((item) => {
        return (
            <IconButton onClick={() => removeDocument(item)} icon="mi-delete" />
        );
    },
    [removeDocument]);

    const claimsDocUploadToken = useCallback(async () => {
        try {
            const uploadTokenID = await ClaimService.claimsDocUploadToken(token, sessionUUID);
            return uploadTokenID;
        } catch (e) {
            return handleError(
                commonMessages.errorUploadTitle,
                commonMessages.errorGenerateUploadToken
            );
        }
    }, [ClaimService, handleError, token, sessionUUID]);

    const getNormalizedFileName = useCallback((fileName) => {

        const fileNameSplitted = fileName
            .replace(/[^a-zA-Z0-9.]/g, '_')
            .split('.');
        const fileExtension = _.last(fileNameSplitted);
        fileNameSplitted.pop();
        let fileNameWithoutSpecialCharacters = fileNameSplitted.join('_');
        return fileNameWithoutSpecialCharacters + "." + fileExtension;
    }, []);

    const handleDocumentPick = useCallback(async (file) => {
        if (documentsToSend.length === 10) {
            handleError(
                translator(commonMessages.errorUploadTitle),
                translator(messages.maxFilesCountError)
            );
            return;
        }
        if (file.size > MAX_UPLOAD_FILE_SIZE) {
            handleError(
                translator(commonMessages.errorUploadTitle),
                translator(messages.failedUploadDocumentsMaxSize, {
                    size: MAX_UPLOAD_FILE_SIZE / 1000
                })
            );
            return;
        }

        const fileNameParts = file.name.split('.');
        if (fileNameParts.length < 2 || !ALLOWED_FILE_FORMATS
            .includes(_.lowerCase(_.last(fileNameParts)))) {
            handleError(
                translator(commonMessages.errorUploadTitle),
                translator(messages.failedUploadDocumentsFileFormat, {
                    documentArray: file.name
                })
            );
            return;
        }

        // file.type is a type that browser assigned
        let fileType = file.type;

        // If there is no file type, it means that browser did not understood what kind of file was uploaded, for example HEIC or HEIF
        if (_.isEmpty(fileType)) {
            const fileExtension = fileNameParts[fileNameParts.length - 1];

            const matchingEntryFromFormatMap = fileFormatsMap.fileFormats.find((formatFromMap) => formatFromMap.extension === fileExtension.toLowerCase());
            if (matchingEntryFromFormatMap !== undefined) {
                fileType = matchingEntryFromFormatMap.mimeType;
            }
        }

        const fileWithNormalizedName = new File([file], getNormalizedFileName(file.name), { type: fileType });

        const document = {
            documentType: 'fnol',
            token,
            Status: 'approved',
            name: fileWithNormalizedName.name,
            mimeType: fileWithNormalizedName.type,
            file: fileWithNormalizedName,
            documentCategory_Adac: documentCategory,
            sessionID: await claimsDocUploadToken()
        };

        updateDocumentsToSend((prevDocs) => [...prevDocs, document]);
        setDocumentCategory(null);
    },
    [
        documentCategory,
        updateDocumentsToSend,
        documentsToSend,
        claimsDocUploadToken,
        location,
        handleError,
        setDocumentCategory,
        translator
    ]);

    const getCategoryCell = useCallback((item) => {
        return (
            <span>
                {translator({
                    id: `typekey.PortalDocumentType_Adac.${item.documentCategory_Adac}`,
                    defaultMessage: item.documentCategory
                })}
            </span>
        );
    }, [translator]);

    const getDataCell = useCallback((item, index, { path: property }) => {
        return (
            <span className={styles.filename}>
                {item[property]}
            </span>
        );
    },
    []);

    const onCancel = useCallback(() => {
        setNewDocument(false);
        setDocumentCategory(null);
    }, []);

    useEffect(() => {
        if (hideCancel) {
            outerCancel.current = onCancel;
        }
    }, [outerCancel, onCancel, hideCancel]);

    const overrides = {
        newDocumentContainer: {
            visible: newDocument
        },
        uploadDocumentType: {
            visible: newDocument,
            value: documentCategory,
            onValueChange: setDocumentCategory,
            availableValues: documentTypes
        },
        addNewDocumentButton: {
            onClick: () => setNewDocument(true),
            visible: !newDocument
        },
        uploadDocumentsButton: {
            disabled: !documentCategory
        },
        cancelButton: {
            visible: newDocument && !hideCancel
        },
        documentsTableGrid: {
            data: documentsToSend
        },
        documentsTableContainer: {
            visible: documentsToSend.length > 0
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
        },
        resolveCallbackMap: {
            getRemove,
            getCategoryCell,
            getDataCell,
            handleDocumentPick,
            onCancel
        }
    };
    return <MetadataContent
        uiProps={metadata.pageContent}
        overrideProps={overrides}
        {...resolvers}
    />;
}

export default withRouter(DocumentsUpload);
